/**
 * Call page shortcuts (spec §4.6, DES-3, DES-4). Pure: the page passes the
 * key event and where the focus is, and gets back what to do. Keys are read
 * on event.code, so the digits work on an AZERTY keyboard.
 *
 * - In a text field only the modifier shortcuts run (⌘/Ctrl+P, and
 *   ⌘/Ctrl+Entrée which saves the note there and never pauses); Échap leaves
 *   the field.
 * - A button keeps its own Entrée/Espace. The outcome pad is a radiogroup:
 *   1–8 and Entrée act there (the exception written in ADR-0047).
 * - An open dialog suspends every shortcut and the countdown (DES-4).
 * - Single-key shortcuts can be turned off (WCAG 2.1.4); the pad keeps its
 *   digits while it has the focus.
 * - Échap never hangs up.
 */

export type CallPagePhase = 'prep' | 'dialing' | 'connected' | 'qualifying' | 'paused' | 'finished';
export type FocusKind = 'text' | 'button' | 'pad' | 'other';

export type CallPageAction =
  | { type: 'session-toggle' }
  | { type: 'save-note' }
  | { type: 'hangup' }
  | { type: 'hangup-qualify' }
  | { type: 'mute' }
  | { type: 'keypad' }
  | { type: 'focus-note' }
  | { type: 'outcome'; index: number }
  | { type: 'save' }
  | { type: 'extend' }
  | { type: 'undo' }
  | { type: 'cancel-countdown' }
  | { type: 'blur' }
  | { type: 'help' };

export interface KeyInput {
  code: string;
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  altKey?: boolean;
}

export interface KeyContext {
  phase: CallPagePhase;
  focus: FocusKind;
  /** A countdown before the next series (Espace, Échap act on it). */
  countdown: boolean;
  /** « Annuler » is still possible (U). */
  canUndo?: boolean;
  dialogOpen: boolean;
  keypadOpen: boolean;
  singleKeys: boolean;
}

const DIGIT = /^(?:Digit|Numpad)([1-8])$/;

export function resolveCallPageKey(e: KeyInput, ctx: KeyContext): CallPageAction | null {
  if (ctx.dialogOpen) return null;
  const mod = !!(e.metaKey || e.ctrlKey);
  const inText = ctx.focus === 'text';
  const onCall = ctx.phase === 'connected';

  if (mod && !e.altKey) {
    if (e.code === 'KeyP' && onCall) return { type: 'hangup-qualify' };
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      if (inText) return { type: 'save-note' };
      if (ctx.phase === 'prep' || ctx.phase === 'paused' || ctx.phase === 'dialing') return { type: 'session-toggle' };
    }
    return null;
  }

  if (e.code === 'Escape') {
    if (inText) return { type: 'blur' };
    if (ctx.countdown) return { type: 'cancel-countdown' };
    return null;
  }
  if (inText || e.altKey) return null;

  const digit = DIGIT.exec(e.code);
  if (digit && !ctx.keypadOpen && (ctx.phase === 'qualifying' || onCall)) {
    if (ctx.singleKeys || ctx.focus === 'pad') return { type: 'outcome', index: Number(digit[1]) - 1 };
    return null;
  }
  if (e.code === 'Enter' || e.code === 'NumpadEnter') {
    if (ctx.phase === 'qualifying' && !ctx.countdown && (ctx.focus === 'pad' || ctx.focus === 'other')) return { type: 'save' };
    return null;
  }
  if (e.code === 'Space') {
    if (ctx.countdown && ctx.focus !== 'button') return { type: 'extend' };
    return null;
  }
  // « ? » activates no button: it opens the help from anywhere outside a field.
  if (e.key === '?' && ctx.singleKeys) return { type: 'help' };
  if (ctx.focus === 'button' || !ctx.singleKeys) return null;

  switch (e.code) {
    case 'KeyH': return onCall ? { type: 'hangup' } : null;
    case 'KeyM': return onCall ? { type: 'mute' } : null;
    case 'KeyD': return onCall ? { type: 'keypad' } : null;
    case 'KeyN': return onCall || ctx.phase === 'qualifying' ? { type: 'focus-note' } : null;
    case 'KeyU': return ctx.canUndo ? { type: 'undo' } : null;
    default: return null;
  }
}

/** Where the focus is, from the event target. */
export function focusKindOf(target: EventTarget | null): FocusKind {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return 'other';
  if (el.closest('[role="radiogroup"][data-call-pad]')) return 'pad';
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || el.isContentEditable) return 'text';
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return ['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type) ? 'button' : 'text';
  }
  if (tag === 'SELECT') return 'text';
  if (tag === 'BUTTON' || el.getAttribute('role') === 'button' || tag === 'A') return 'button';
  return 'other';
}

const SINGLE_KEYS_STORAGE = 'xdial_call_single_keys';

export function readSingleKeysPref(): boolean {
  try { return localStorage.getItem(SINGLE_KEYS_STORAGE) !== 'off'; } catch { return true; }
}

export function writeSingleKeysPref(on: boolean): void {
  try { localStorage.setItem(SINGLE_KEYS_STORAGE, on ? 'on' : 'off'); } catch { /* per-viewer convenience */ }
}
